import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { formatDay } from '@lensmira/i18n'
import { uuidv7 } from 'uuidv7'

import { useTranslation } from '../i18n.js'
import { DataSurface } from './DataSurface.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { EnrolTerminalResult } from '../session/session.js'

/**
 * Enrolling a counter terminal. `TEN-006`, `decisions/0021`.
 *
 * **Enrolling is the one write on this screen that does not go through the write queue**, and the
 * reason is the answer: it carries the terminal's token, once, and the queue keeps a write's
 * response on the record in IndexedDB. `onEnrol` is given by the shell, which owns the session and
 * the fetch (`session/session.ts` names the exception). Revoking has no secret in its answer and is
 * an ordinary queued write.
 *
 * **The token is shown once and kept nowhere else on this screen.** Closing the panel drops it from
 * state. Nothing can show it again, so a code that was not taken down means revoking the terminal
 * and enrolling it again, which the text says before the owner presses anything.
 *
 * A device becomes "the terminal" by holding the token in local storage. That is the shell's
 * (`onUseHere`), because storage is not a thing a screen touches. The owner usually enrols from the
 * till itself, which is why the panel can hand the token straight to it; a code carried from
 * another device is pasted into the same place.
 */

export interface TerminalRow {
  readonly id: string
  readonly branchId: string
  readonly name: string
  readonly enrolledAt: string
  readonly revokedAt: string | null
  readonly revokedReason: string | null
  /** `ARC-012`'s lock, sent back so a stale screen is a conflict and not an overwrite. */
  readonly version: number
}

export interface TerminalsProps {
  readonly timezone: string
  readonly loadTerminals?: () => Promise<readonly TerminalRow[]>
  /** Takes the name and the idempotency key; the shell knows the branch, the session and the fetch. */
  readonly onEnrol: (name: string, idempotencyKey: string) => Promise<EnrolTerminalResult>
  /** Whether this device already holds a terminal token. */
  readonly hasLocalToken: boolean
  readonly onUseHere: (token: string) => void
  readonly onForgetHere: () => void
}

export function Terminals({
  timezone,
  loadTerminals,
  onEnrol,
  hasLocalToken,
  onUseHere,
  onForgetHere,
}: TerminalsProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [terminals, setTerminals] = useState<readonly TerminalRow[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [shown, setShown] = useState<{ readonly name: string; readonly token: string } | null>(null)
  const [pasted, setPasted] = useState('')
  const [revoking, setRevoking] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [writeId, setWriteId] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /**
   * One key per attempt, kept until the attempt succeeds, so a double tap or a retry after a lost
   * answer enrols one terminal (`ARC-006`). Cleared on success so the next terminal gets its own.
   */
  const attemptKey = useRef<string | null>(null)

  const refresh = useCallback(() => {
    if (loadTerminals === undefined) return
    // Cleared first so a retry shows the skeleton again and does not look like nothing happened.
    setFailed(false)
    void loadTerminals()
      .then((loaded) => {
        setTerminals(loaded)
        setFailed(false)
      })
      .catch(() => {
        // Not written to `error`: that paragraph belongs to enrol and revoke, and the surface
        // already says the read failed.
        setFailed(true)
      })
  }, [loadTerminals])

  useEffect(() => {
    refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setRevoking(null)
    setReason('')
    refresh()
  })

  const enrol = useCallback(async () => {
    if (name.trim() === '') {
      setError(t('terminals.nameRequired'))
      return
    }
    setError(null)
    setDone(null)
    setBusy(true)
    attemptKey.current ??= uuidv7()
    const result = await onEnrol(name.trim(), attemptKey.current)
    setBusy(false)

    if (result.kind === 'enrolled') {
      attemptKey.current = null
      setShown({ name: result.name, token: result.token })
      setName('')
      refresh()
      return
    }
    // The server's own words: a name already taken, a branch not held, an unreachable server.
    setError(result.message)
  }, [name, onEnrol, refresh, t])

  const revoke = useCallback(
    async (terminal: TerminalRow) => {
      if (reason.trim() === '') {
        setError(t('terminals.reasonRequired'))
        return
      }
      setError(null)
      setDone(null)
      const write = (await enqueue({
        method: 'POST',
        path: `/terminals/${encodeURIComponent(terminal.id)}/revocation`,
        label: t('terminals.queueRevokeLabel', { name: terminal.name }),
        body: { reason: reason.trim(), expectedVersion: terminal.version },
      })) as { id: string }
      setWriteId(write.id)
      setDone(t('terminals.revokeQueued', { name: terminal.name }))
    },
    [enqueue, reason, t],
  )

  const line = statusLine(t, snapshot, writeId, t('queue.subject.setup'))

  return (
    <section className="lm-screen" aria-labelledby="terminals-heading">
      <h2 id="terminals-heading" className="lm-setup-panel__heading">
        {t('terminals.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('terminals.means')}</p>

      {/*
        The enrol form below stays rendered in every state: it is how the first terminal gets a
        code. With no loader nothing is ever read, so there is no surface to draw.
      */}
      {loadTerminals === undefined ? null : (
        <DataSurface
          state={
            failed
              ? {
                  kind: 'error',
                  message: t('terminals.loadFailed'),
                  onRetry: refresh,
                }
              : terminals === null
                ? { kind: 'loading' }
                : terminals.length === 0
                  ? { kind: 'empty' }
                  : { kind: 'ready' }
          }
          shape="rows"
          rows={2}
          what={t('terminals.what')}
          emptyMessage={t('terminals.emptyInvite')}
        >
          <ul className="lm-setup-panel__people">
            {(terminals ?? []).map((terminal) => (
              <li key={terminal.id} className="lm-setup-person">
                <span className="lm-setup-person__name" dir="auto">
                  {terminal.name}
                </span>
                <span className="lm-setup-person__meta">
                  {terminal.revokedAt === null
                    ? t('terminals.enrolledOn', {
                        date: formatDay(terminal.enrolledAt, timezone, locale),
                      })
                    : t('terminals.revokedOn', {
                        date: formatDay(terminal.revokedAt, timezone, locale),
                        reason: terminal.revokedReason ?? '',
                      })}
                </span>
                {terminal.revokedAt !== null ? null : revoking === terminal.id ? (
                  <div className="lm-setup-person__confirm">
                    <p className="lm-setup-panel__effects">
                      {t('terminals.revokeMeans', { name: terminal.name })}
                    </p>
                    <label className="lm-field">
                      <span className="lm-field__label">{t('terminals.revokeReason')}</span>
                      <input
                        className="lm-input"
                        dir="auto"
                        value={reason}
                        autoComplete="off"
                        onChange={(e) => setReason(e.currentTarget.value)}
                      />
                    </label>
                    <button
                      type="button"
                      className="lm-button lm-button--danger"
                      disabled={writeId !== null}
                      onClick={() => {
                        void revoke(terminal)
                      }}
                    >
                      {t('terminals.revokeConfirm')}
                    </button>
                    <button
                      type="button"
                      className="lm-button lm-button--secondary"
                      onClick={() => {
                        setRevoking(null)
                        setReason('')
                      }}
                    >
                      {t('terminals.revokeCancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="lm-button lm-button--secondary"
                    onClick={() => {
                      setRevoking(terminal.id)
                      setReason('')
                    }}
                  >
                    {t('terminals.revoke', { name: terminal.name })}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </DataSurface>
      )}

      {shown === null ? (
        <div className="lm-setup-panel">
          <label className="lm-field">
            <span className="lm-field__label">{t('terminals.nameLabel')}</span>
            <input
              className="lm-input"
              dir="auto"
              value={name}
              autoComplete="off"
              onChange={(e) => setName(e.currentTarget.value)}
            />
          </label>
          <button
            type="button"
            className="lm-button lm-button--primary"
            disabled={busy}
            onClick={() => {
              void enrol()
            }}
          >
            {busy ? t('terminals.enrolling') : t('terminals.enrol')}
          </button>
        </div>
      ) : (
        <section className="lm-enrol" aria-labelledby="terminal-token-heading">
          <h3 id="terminal-token-heading" className="lm-enrol__heading">
            {t('terminals.enrolled', { name: shown.name })}
          </h3>
          <p className="lm-enrol__means">{t('terminals.tokenMeans')}</p>
          <span className="lm-field__label">{t('terminals.tokenLabel')}</span>
          {/* Selectable and in the data face, because it may be carried to a till by hand.
              `dir="ltr"`: the code is Latin whatever the page direction. */}
          <code className="lm-enrol__secret" dir="ltr">
            {shown.token}
          </code>
          <button
            type="button"
            className="lm-button lm-button--primary"
            onClick={() => {
              onUseHere(shown.token)
              setShown(null)
              setDone(t('terminals.usingHere'))
            }}
          >
            {t('terminals.useHere')}
          </button>
          <button
            type="button"
            className="lm-button"
            onClick={() => {
              setShown(null)
            }}
          >
            {t('terminals.tokenDone')}
          </button>
        </section>
      )}

      <div className="lm-setup-panel">
        <label className="lm-field">
          <span className="lm-field__label">{t('terminals.pasteLabel')}</span>
          <input
            className="lm-input"
            dir="ltr"
            value={pasted}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setPasted(e.currentTarget.value)}
          />
        </label>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          disabled={pasted.trim() === ''}
          onClick={() => {
            onUseHere(pasted.trim())
            setPasted('')
            setDone(t('terminals.usingHere'))
          }}
        >
          {t('terminals.pasteUse')}
        </button>
        {hasLocalToken ? (
          <>
            <p className="lm-hint">{t('terminals.thisDevice')}</p>
            <button
              type="button"
              className="lm-button lm-button--secondary"
              onClick={() => {
                onForgetHere()
                setDone(t('terminals.forgotten'))
              }}
            >
              {t('terminals.forgetHere')}
            </button>
          </>
        ) : null}
      </div>

      {error === null ? null : <p className="lm-error">{error}</p>}
      {done === null ? null : <p className="lm-setup-panel__effects">{done}</p>}
      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}
    </section>
  )
}
